import { exponantialBackOff } from '@climatepartner/unified-api-exponantial-backoff'
import { EnvVarNames } from '@climatepartner/unified-api-service/shared'

import { IOrderErrorNotFoundCodes } from '../gen/interface/components/i-order-error-not-found-codes'
import { IOrderInfoRequestHeader } from '../gen/interface/components/i-order-info-request-header'
import { IOrderRequest } from '../gen/interface/components/i-order-request'
import { IOrderStatusEnum } from '../gen/interface/components/i-order-status-enum'
import { IOrderSubOrderRequest } from '../gen/interface/components/i-order-sub-order-request'
import { IOrderUpdateInfoSubOrderRequest } from '../gen/interface/components/i-order-update-info-sub-order-request'
import { IDeleteIndividualOrderPathParameter } from '../gen/interface/parameter/i-delete-individual-order-path-parameter'
import { IDeleteSubOrderPathParameter } from '../gen/interface/parameter/i-delete-sub-order-path-parameter'
import { IUpdateCollectiveOrderPathParameter } from '../gen/interface/parameter/i-update-collective-order-path-parameter'
import { IUpdateIndividualOrderPathParameter } from '../gen/interface/parameter/i-update-individual-order-path-parameter'
import { IUpdateSubOrderPathParameter } from '../gen/interface/parameter/i-update-sub-order-path-parameter'
import { OrderBaseService } from '../gen/service/order-base-service'
import { CreateOrderErrorCode, UnifiedApiError } from '../types'

const allowedStatuses = [
  IOrderStatusEnum.CREATE_SUCCESSFUL,
  IOrderStatusEnum.CREATE_FAILED,
  IOrderStatusEnum.DELETE_SUCCESSFUL,
  IOrderStatusEnum.DELETE_FAILED,
  IOrderStatusEnum.UPDATE_SUCCESSFUL,
  IOrderStatusEnum.UPDATE_FAILED,
]

type GetDeleteOrderResponse = { result: { lastActionStatus: IOrderStatusEnum } }
export class OrderService extends OrderBaseService {
  async createIndividualOrderSync(body: IOrderRequest) {
    return this.#orderActionSync(
      this.createIndividualOrder,
      [body],
      createOrderResponse =>
        this.getIndividualOrder({
          orderId: createOrderResponse.result.orderId,
        }),
    )
  }

  async createCollectiveOrderSync(body: IOrderInfoRequestHeader) {
    return this.#orderActionSync(
      this.createCollectiveOrder,
      [body],
      createOrderResponse =>
        this.getCollectiveOrder(
          {
            collectiveOrderId: createOrderResponse.result.collectiveOrderId,
          },
          {},
        ),
    )
  }

  async createSubOrderSync(body: IOrderSubOrderRequest) {
    return this.#orderActionSync(
      this.createSubOrder,
      [body],
      createOrderResponse =>
        this.getSubOrder({
          orderId: createOrderResponse.result.orderId,
        }),
    )
  }

  async updateIndividualOrderSync(
    pathParameters: IUpdateIndividualOrderPathParameter,
    body: IOrderUpdateInfoSubOrderRequest,
  ) {
    return this.#orderActionSync(
      this.updateIndividualOrder,
      [pathParameters, body],
      updateIndividualOrderResponse =>
        this.getIndividualOrder({
          orderId: updateIndividualOrderResponse.result.orderId,
        }),
    )
  }

  async updateCollectiveOrderSync(
    pathParameters: IUpdateCollectiveOrderPathParameter,
    body: IOrderInfoRequestHeader,
  ) {
    return this.#orderActionSync(
      this.updateCollectiveOrder,
      [pathParameters, body],
      updateCollectiveOrderResponse =>
        this.getCollectiveOrder(
          {
            collectiveOrderId:
              updateCollectiveOrderResponse.result.collectiveOrderId,
          },
          {},
        ),
    )
  }

  async updateSubOrderSync(
    pathParameters: IUpdateSubOrderPathParameter,
    body: IOrderUpdateInfoSubOrderRequest,
  ) {
    return this.#orderActionSync(
      this.updateSubOrder,
      [pathParameters, body],
      updateSubOrderResponse =>
        this.getSubOrder({
          orderId: updateSubOrderResponse.result.orderId,
        }),
    )
  }

  async #pollDeletedOrder(
    getOrderFunction: () => Promise<any>,
    // TODO Add the missing return
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  ): Promise<GetDeleteOrderResponse> {
    try {
      const result = await getOrderFunction()
      if (result.result.lastActionStatus === IOrderStatusEnum.DELETE_FAILED) {
        return result
      }
    } catch (error) {
      if (error instanceof UnifiedApiError) {
        if (error.code === IOrderErrorNotFoundCodes.ORDER_DELETED) {
          //Question is, can and should we return api response? from here?
          return {
            result: {
              lastActionStatus: IOrderStatusEnum.DELETE_SUCCESSFUL,
            },
          }
        }
      }
      throw error
    }
  }

  async deleteIndividualOrderSync(
    pathParameters: IDeleteIndividualOrderPathParameter,
  ) {
    return this.#orderActionSync(
      this.deleteIndividualOrder,
      [pathParameters],
      deleteIndividualOrderResponse =>
        this.#pollDeletedOrder(() =>
          this.getIndividualOrder({
            orderId: deleteIndividualOrderResponse.result.orderId,
          }),
        ),
    )
  }

  async deleteSubOrderSync(pathParameters: IDeleteSubOrderPathParameter) {
    return this.#orderActionSync(
      this.deleteSubOrder,
      [pathParameters],
      deleteSubOrderResponse =>
        this.#pollDeletedOrder(() =>
          this.getSubOrder({
            orderId: deleteSubOrderResponse.orderId,
          }),
        ),
    )
  }

  async #orderActionSync<
    OrderActionFunction extends (...args: any) => Promise<any>,
    GetOrderResponse extends
      | Awaited<ReturnType<OrderBaseService['getIndividualOrder']>>
      | Awaited<ReturnType<OrderBaseService['getCollectiveOrder']>>
      | Awaited<ReturnType<OrderBaseService['getSubOrder']>>
      | GetDeleteOrderResponse,
  >(
    orderActionFunction: OrderActionFunction,
    orderActionArgs: Parameters<OrderActionFunction>,
    getOrderFunction: (
      createOrderResponse: Awaited<ReturnType<OrderActionFunction>>,
    ) => Promise<GetOrderResponse>,
  ) {
    try {
      // Run order action (ex. create order, update order)
      const createOrderResponse = await orderActionFunction.apply(
        this,
        orderActionArgs,
      )

      // Repeatadly get order until status is final (state success or failure)
      const startingDelay =
        parseInt(process.env[EnvVarNames.SDK_ASYNC_DELAY_START] as string) ||
        100 // 100ms
      const maximumDelay =
        parseInt(process.env[EnvVarNames.SDK_ASYNC_MAX_DELAY] as string) ||
        1000 * 4 // 4000ms = 4s
      /**
       * Total possible delay with defaults (14.3s) calculation:
       * 100 + 200 + 400 + 800 + 1600 + 3200 = 6300ms
       * after this the delay would exceed 4000ms (maximumDelay), so from here 4000 gets applied each retry
       * 6300 + 4000 + 4000 = 14300ms = 14.3s
       */
      const numberOfAttempts =
        parseInt(process.env[EnvVarNames.SDK_ASYNC_MAX_ATTEMPTS] as string) ||
        10

      const getOrderResponse = await exponantialBackOff(
        async () => {
          const getOrderResponse = await getOrderFunction(createOrderResponse)

          if (
            !getOrderResponse?.result?.lastActionStatus ||
            !allowedStatuses.includes(
              getOrderResponse?.result?.lastActionStatus,
            )
          ) {
            throw new UnifiedApiError(
              0,
              CreateOrderErrorCode.REQUEST_TIMED_OUT,
              'Order processing did not finish in time',
            )
          }

          return getOrderResponse
        },
        {
          numberOfAttempts,
          startingDelay,
          maximumDelay,
        },
      )

      return getOrderResponse
    } catch (e: any) {
      if (e instanceof UnifiedApiError) {
        throw e
      }
      throw new UnifiedApiError(
        0,
        CreateOrderErrorCode.INTERNAL_ERROR,
        e.message,
      )
    }
  }
}
